import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { Translate } from '../../../shared/LanguageService';
import { MAX_CLONE_COUNT, MIN_CLONE_COUNT, useCloneOrderModal } from './useCloneOrderModal';
import { CloneOrderDetailsForm } from './CloneOrderDetailsForm';

interface IProps {
    show: boolean;
    onHide: () => void;
    orderId: number;
    onResult: (success: boolean) => void;
}

export const CloneOrderModal = (props: IProps) => {
    const {
        count,
        changeCount,
        copies,
        selectedIndex,
        setSelectedIndex,
        selectedCopy,
        updateSelected,
        applyToAll,
        isModified,
        objectTypeOptions,
        isCommercial,
        isLoading,
        isSourceLoading,
        isSourceError,
        refetchSource,
        isCountValid,
        isCreateDisabled,
        handleCreate
    } = useCloneOrderModal({
        orderId: props.orderId,
        show: props.show,
        onHide: props.onHide,
        onResult: props.onResult
    });

    return (
        <Dialog open={props.show} onClose={props.onHide} fullWidth maxWidth="md">
            <DialogTitle sx={{ fontWeight: 700 }}>
                {Translate('view-order/clone/modal/title')}
            </DialogTitle>
            <DialogContent>
                <Typography mb={2}>{Translate('view-order/clone/modal/description')}</Typography>
                <TextField
                    autoFocus
                    type="number"
                    label={Translate('view-order/clone/modal/label-count')}
                    value={Number.isNaN(count) ? '' : count}
                    onChange={(e) => changeCount(parseInt(e.target.value, 10))}
                    error={!isCountValid}
                    inputProps={{ min: MIN_CLONE_COUNT, max: MAX_CLONE_COUNT, step: 1 }}
                    sx={{ width: 160 }}
                />

                {isSourceLoading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                    </Box>
                ) : isSourceError ? (
                    <Box py={4}>
                        <Alert
                            severity="error"
                            action={
                                <Button color="inherit" size="small" onClick={() => refetchSource()}>
                                    {Translate('view-order/clone/modal/retry')}
                                </Button>
                            }
                        >
                            {Translate('view-order/clone/error/source-load-failed')}
                        </Alert>
                    </Box>
                ) : (
                    <Box display="flex" gap={2} mt={2} sx={{ minHeight: 320 }}>
                        <List
                            dense
                            sx={{
                                width: 160,
                                flexShrink: 0,
                                maxHeight: 360,
                                overflowY: 'auto',
                                borderRight: 1,
                                borderColor: 'divider'
                            }}
                        >
                            {copies.map((copy, index) => (
                                <ListItemButton
                                    key={index}
                                    selected={index === selectedIndex}
                                    onClick={() => setSelectedIndex(index)}
                                >
                                    <ListItemText
                                        primary={Translate('view-order/clone/modal/copy-label', {
                                            n: index + 1
                                        })}
                                    />
                                    {isModified(copy) && (
                                        <Box
                                            component="span"
                                            sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: '50%',
                                                bgcolor: 'primary.main'
                                            }}
                                        />
                                    )}
                                </ListItemButton>
                            ))}
                        </List>

                        <Box flexGrow={1}>
                            {selectedCopy && (
                                <>
                                    <CloneOrderDetailsForm
                                        value={selectedCopy}
                                        onChange={updateSelected}
                                        objectTypeOptions={objectTypeOptions}
                                        isCommercial={isCommercial}
                                        disabled={isLoading}
                                    />
                                    {copies.length > 1 && (
                                        <Button
                                            size="small"
                                            onClick={applyToAll}
                                            disabled={isLoading}
                                            sx={{ mt: 2 }}
                                        >
                                            {Translate('view-order/clone/modal/apply-to-all')}
                                        </Button>
                                    )}
                                </>
                            )}
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={props.onHide} disabled={isLoading}>
                    {Translate('view-order/clone/modal/cancel')}
                </Button>
                <Button variant="contained" onClick={handleCreate} disabled={isCreateDisabled}>
                    {Translate('view-order/clone/modal/create')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
